import { ChevronRightSVG } from '@sbercloud/uikit-product-icons';
import { ButtonFunction } from '@snack-uikit/button';
import { DroplistProps, List } from '@snack-uikit/list';
import { Scroll } from '@snack-uikit/scroll';
import { Tabs } from '@snack-uikit/tabs';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SOLUTION_CATEGORIES } from 'src/components/SolutionsBlock/solutionCategories';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useHeaderPosition } from 'src/hooks/useHeaderPosition';
import { useWindowWidth } from 'src/hooks/useWindowWidth';
import { HeaderCardProduct } from 'src/layout/Header/components/HeaderCardProduct';
import { ProductsSectionProps } from 'src/layout/Header/types';
import { type CatalogProduct } from 'src/types/backendContent';

import s from './Products.module.scss';

const HEADER_DESKTOP_WIDTH = 1269;

const MAX_RENDER_SOLUTIONS_COUNT = 8;

function SolutionsButton({
  onClick,
  countSolutions,
}: {
  onClick?: () => void;
  countSolutions: number;
}) {
  const { clickAnalytics, customAnalytics } = useAnalytics();
  const router = useRouter();
  const href = '/solutions';
  const label = `Все решения (${countSolutions})`;

  return (
    <ButtonFunction
      label={label}
      href={href}
      onClick={(e) => {
        e.preventDefault();
        onClick && onClick();

        customAnalytics('AB_solutions_card_clicks', ['']);

        clickAnalytics({
          action: href,
          clickZone: 'header',
          clickElement: 'button',
          clickContent: label,
          uniqueId: 'header-menu-solutions-button-to-all-solutions',
          transitionType: 'inside-link',
          transitionUrl: href,
          transitionTypeMetrics: 'internal',
          clickZoneCategory: 'site-header',
        });
        router.push(href);
      }}
      size="m"
      appearance="neutral"
      icon={<ChevronRightSVG />}
      iconPosition="after"
      className={s.allProductsButton}
    />
  );
}

type CategoryListProps = {
  categories: {
    id: string;
    title: string;
  }[];
  value: string | undefined;
  singleMode?: boolean;
  onChange: (value: string) => void;
  onOpenChange?: (value: boolean) => void;
  classNames?: {
    root?: string;
  };
};

function CategoryList({
  categories,
  value,
  singleMode,
  onChange,
  classNames,
}: CategoryListProps) {
  const items: DroplistProps['items'] = categories.map(({ id, title }) => ({
    id: id,
    content: { option: title, truncate: { option: 2 } },
  }));

  const handleChange = (newValue: string) => {
    if (singleMode && (newValue === value || !newValue)) {
      return;
    } else {
      onChange(newValue);
    }
  };

  return (
    <div className={cn(s.root, classNames?.root)}>
      <List
        size="m"
        items={items}
        selection={{ value, onChange: handleChange, mode: 'single' }}
        scroll
        className={s.list}
      />
    </div>
  );
}

function Menu({
  categories,
  categoryFilter,
  onCategoryFilterChange,
  onClickForClose,
  countSolutions,
}: {
  categories: {
    id: string;
    title: string;
  }[];
  categoryFilter: string;
  onCategoryFilterChange: (categoryId: string) => void;
  onClickForClose?: () => void;
  countSolutions: number;
}) {
  const { headerHeight } = useHeaderPosition();

  return (
    <div
      className={s.menu}
      style={{
        maxHeight: `calc(100vh - ${headerHeight}px - 104px)`,
      }}
    >
      <CategoryList
        categories={categories}
        value={categoryFilter}
        onChange={onCategoryFilterChange}
        singleMode
        classNames={{
          root: s.categoryList,
        }}
      />

      <div className={s.additionalLinks}>
        <SolutionsButton
          onClick={onClickForClose}
          countSolutions={countSolutions}
        />
      </div>
    </div>
  );
}

function CategoryTitleSearch({
  sectionTitle,
  count,
  onClickForClose,
}: {
  sectionTitle: string;
  count: number;
  onClickForClose?: () => void;
}) {
  const { clickAnalytics, customAnalytics } = useAnalytics();
  const router = useRouter();

  const buttonLabelText = 'задачи ';
  const uniqueId = 'header-menu-solutions-button-to-all';

  const href = '/solutions';

  const buttonLabel =
    count > MAX_RENDER_SOLUTIONS_COUNT
      ? `Еще ${buttonLabelText} (${count - MAX_RENDER_SOLUTIONS_COUNT})`
      : `Еще ${buttonLabelText}`;

  return (
    <div
      className={cn(s.blockHeader, s.blockHeaderSmall, {
        [s.notFoundTitle]: count < 1,
      })}
    >
      <Typography.SansTitleL className={s.categorySectionTitle}>
        {sectionTitle}
      </Typography.SansTitleL>

      {count - MAX_RENDER_SOLUTIONS_COUNT > 0 && (
        <ButtonFunction
          label={buttonLabel}
          href={href}
          onClick={(e) => {
            e.preventDefault();

            customAnalytics('AB_solutions_card_clicks', ['']);

            clickAnalytics({
              action: href,
              clickZone: 'header',
              clickElement: 'button',
              clickContent: `Еще ${buttonLabelText}`,
              uniqueId: uniqueId,
              transitionType: 'inside-link',
              transitionUrl: href,
              transitionTypeMetrics: 'internal',
              clickZoneCategory: 'site-header',
            });
            onClickForClose && onClickForClose();
            router.push(href);
          }}
          size="m"
          appearance="neutral"
          icon={<ChevronRightSVG />}
          iconPosition="after"
        />
      )}
    </div>
  );
}

function ProductsList({
  products,
  category,
  categoryTitle,
  onClickForClose,
}: {
  products: CatalogProduct[];
  category: CatalogProduct['solutionType'];
  categoryTitle: string;
  onClickForClose?: () => void;
}) {
  const { clickAnalytics, customAnalytics } = useAnalytics();

  const onCardClick = (title: string, link: string) => {
    //TODO: удалить после тестов
    customAnalytics('AB_solutions_card_clicks', ['']);
    clickAnalytics({
      action: link,
      clickZone: 'header',
      clickElement: 'cardclick',
      clickContent: title,
      uniqueId: `header-menu-solutions-card-click-${title}`,
      transitionType: 'inside-link',
      transitionUrl: link,
      transitionTypeMetrics: 'internal',
      clickZoneCategory: 'site-header',
    });
    onClickForClose && onClickForClose();
  };

  const renderHeaderCardProduct = (product: CatalogProduct) => {
    const href = product.customLink
      ? `/${product.customLink}`
      : `/solutions/${product.slug}`;

    const filteredTags = product?.tags
      ? product.tags.filter((tag) => !tag.hideInCatalog)
      : [];

    return (
      <HeaderCardProduct
        key={product.id}
        onClickForClose={() => onCardClick(product.title, href)}
        title={product.title}
        text={product.description}
        icon={product.icon}
        link={href}
        productTags={filteredTags}
      />
    );
  };

  const productsByCategory = products.filter(
    (item) => item.solutionType === category,
  );

  const slicedSolutions = productsByCategory.slice(
    0,
    MAX_RENDER_SOLUTIONS_COUNT,
  );

  return (
    <div className={s.itemsWrapper}>
      <div className={s.block}>
        {productsByCategory.length > 0 && (
          <div>
            <CategoryTitleSearch
              sectionTitle={categoryTitle}
              count={productsByCategory.length}
              onClickForClose={onClickForClose}
            />
            <div className={cn(s.blockGrid)}>
              {slicedSolutions.map(renderHeaderCardProduct)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabletHeader({ products, onClickForClose }: ProductsSectionProps) {
  const categories = structuredClone(SOLUTION_CATEGORIES).reverse();
  const activeCategory = categories[0];
  const [categoryFilter, setCategoryFilter] = useState(activeCategory.id);
  const [categoryTitle, setCategoryTitle] = useState(activeCategory.title);
  const [solutionsProducts, setSolutionsProducts] = useState<CatalogProduct[]>(
    products.items,
  );

  const solutionsItems = products.items.filter(
    (product) => product.type === 'solutions',
  );

  useEffect(() => {
    const productsByCategory = products.items.filter(
      (item) => item.solutionType === categoryFilter,
    );

    setSolutionsProducts(productsByCategory);

    const currentCategory = categories.find((cat) => cat.id === categoryFilter);

    if (currentCategory) setCategoryTitle(currentCategory.title);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  return (
    <div className={s.root}>
      <Tabs value={categoryFilter}>
        <Tabs.TabBar className={s.tabBar}>
          {categories.map((category) => {
            return (
              <Tabs.Tab
                label={category.title}
                value={category.id}
                onClick={() => setCategoryFilter(category.id)}
                key={`${category.id}_${category.title}`}
              />
            );
          })}
        </Tabs.TabBar>
        {categories.map((category, index) => {
          return (
            <Tabs.TabContent
              key={`${category.id}_${index}`}
              value={category.id}
            >
              <ProductsList
                products={solutionsProducts}
                category={categoryFilter as CatalogProduct['solutionType']}
                categoryTitle={categoryTitle}
                onClickForClose={onClickForClose}
              />
            </Tabs.TabContent>
          );
        })}
      </Tabs>

      <div className={s.additionalLinks}>
        <SolutionsButton
          onClick={onClickForClose}
          countSolutions={solutionsItems.length}
        />
      </div>
    </div>
  );
}

function DesktopHeader({ products, onClickForClose }: ProductsSectionProps) {
  const { clickAnalytics } = useAnalytics();

  const { headerHeight } = useHeaderPosition();

  const solutionsItems = products.items.filter(
    (product) => product.type === 'solutions',
  );

  const categories = structuredClone(SOLUTION_CATEGORIES).reverse();
  const activeCategory = categories[0];
  const [categoryFilter, setCategoryFilter] = useState(activeCategory.id);
  const [categoryTitle, setCategoryTitle] = useState(activeCategory.title);

  const handleCategoryFilterChange = (category: string) => {
    if (category) {
      clickAnalytics({
        action: 'click',
        clickZone: 'header',
        clickElement: 'filter-select',
        clickContent: category,
        uniqueId: `header-menu-solutions-category-${category}`,
        transitionType: 'inside-link',
        clickZoneCategory: 'site-header',
      });
    }

    setCategoryFilter(category);

    const currentCategory = categories.find((cat) => cat.id === category);

    if (currentCategory) setCategoryTitle(currentCategory.title);
  };

  return (
    <div className={s.root}>
      <div className={s.content}>
        <Menu
          categories={categories}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={handleCategoryFilterChange}
          onClickForClose={onClickForClose}
          countSolutions={solutionsItems.length}
        />
        <div className={s.contentWrapper}>
          <Scroll size="s">
            <div
              className={s.right}
              style={{
                maxHeight: `calc(100vh - ${headerHeight}px - 104px)`,
              }}
            >
              <ProductsList
                products={products.items}
                category={categoryFilter as CatalogProduct['solutionType']}
                categoryTitle={categoryTitle}
                onClickForClose={onClickForClose}
              />
            </div>
          </Scroll>
        </div>
      </div>
    </div>
  );
}

export function Solutions({ products, onClickForClose }: ProductsSectionProps) {
  const { windowWidth } = useWindowWidth();

  return windowWidth && windowWidth > HEADER_DESKTOP_WIDTH ? (
    <DesktopHeader products={products} onClickForClose={onClickForClose} />
  ) : (
    <TabletHeader products={products} onClickForClose={onClickForClose} />
  );
}
